// cspell:ignore productsku variantsku
import type { RedoAdminRpcClient } from "@redotech/admin-sdk/rpc/client";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import DownloadIcon from "@redotech/redo-icons/download-01.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { toast } from "@redotech/redo-web/alert";
import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import type { RedoBadgeProps } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoCollapseSubsection } from "@redotech/redo-web/arbiter-components/collapse/redo-collapse-section";
import {
  FileUploadState,
  RedoFileUpload,
} from "@redotech/redo-web/arbiter-components/file-upload/redo-file-upload";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoStandaloneTable } from "@redotech/redo-web/arbiter-components/tables/redo-standalone-table";
import type { RedoTableColumn } from "@redotech/redo-web/arbiter-components/tables/redo-table";
import {
  RedoTableBadgeCell,
  RedoTableTextCell,
} from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { StandardRedoTableHeaderCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-header-cells";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useMemo, useRef, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { downloadCsv } from "../../../utils/csv";

type ImportResult = Awaited<ReturnType<RedoAdminRpcClient["importCatalogCsv"]>>;
type ImportInput = Parameters<RedoAdminRpcClient["importCatalogCsv"]>[0];
type ColumnTarget = ImportInput["targets"][number];
type MatchStrategy = NonNullable<ImportInput["match"]>["strategy"];
type AmbiguityPolicy = ImportInput["ambiguityPolicy"];
type VariantField = Extract<ColumnTarget, { kind: "field" }>["field"];

/** Where one CSV column is written. `metafield` needs a namespace and key too. */
type Destination = "metafield" | "productMetafield" | `field:${VariantField}`;

interface ColumnConfig {
  destination: Destination;
  namespace: string;
  key: string;
  type: string;
  /** Blank cell clears the value instead of leaving it alone. */
  clearBlanks: boolean;
}

interface ColumnRow {
  column: string;
  guessedType: string;
  filledRows: number;
  sampleValues: string[];
}

const DEFAULT_CONFIG: ColumnConfig = {
  destination: "metafield",
  namespace: "custom",
  key: "",
  type: "single_line_text_field",
  clearBlanks: false,
};

const DESTINATIONS: { value: Destination; label: string }[] = [
  { value: "metafield", label: "Variant metafield" },
  { value: "productMetafield", label: "Product metafield" },
  { value: "field:sku", label: "SKU" },
  { value: "field:barcode", label: "Barcode" },
  { value: "field:price", label: "Price" },
  { value: "field:compareAtPrice", label: "Compare-at price" },
  { value: "field:cost", label: "Cost per item" },
  { value: "field:weight", label: "Weight" },
];

const METAFIELD_TYPES = [
  "single_line_text_field",
  "multi_line_text_field",
  "number_integer",
  "number_decimal",
  "boolean",
  "date",
  "json",
];

const STRATEGIES: { value: MatchStrategy; label: string }[] = [
  { value: "sku", label: "Variant SKU" },
  { value: "variantId", label: "Shopify variant ID" },
  { value: "handleAndOptions", label: "Product handle + options" },
  { value: "titleAndOptions", label: "Product title + options" },
];

const AMBIGUITY: { value: AmbiguityPolicy; label: string }[] = [
  { value: "skip", label: "Skip them" },
  { value: "all", label: "Write to every match" },
];

function listItem<T extends string | null>(
  value: T,
  label: string,
): RedoListItem<T> {
  return { id: label, type: "text", text: label, value };
}

function usesOptions(strategy: MatchStrategy): boolean {
  return strategy === "handleAndOptions" || strategy === "titleAndOptions";
}

/**
 * Column names that name a real variant field. Without this a column called
 * "Variant SKU" defaults to a metafield and quietly writes custom.variant_sku
 * instead of the SKU.
 */
const FIELD_COLUMN_ALIASES: [Destination, string[]][] = [
  ["field:sku", ["sku", "variantsku", "productsku"]],
  ["field:barcode", ["barcode", "variantbarcode"]],
  ["field:price", ["price", "variantprice"]],
  [
    "field:compareAtPrice",
    ["compareatprice", "variantcompareatprice", "compareat"],
  ],
  ["field:cost", ["cost", "costperitem", "variantcost", "unitcost"]],
  // "grams" is deliberately absent: the writer keeps the variant's existing
  // weight unit, so a grams column would be written as pounds.
  ["field:weight", ["weight", "variantweight"]],
];

/**
 * Shopify names a metafield column "Bin (product.metafields.custom.bin)". The
 * parenthetical is authoritative — the display name in front of it is not.
 */
const METAFIELD_HEADER =
  /\((?:product|variant)\.metafields\.([^.)]+)\.([^)]+)\)/;

function deriveMetafield(column: string): {
  namespace: string;
  key: string;
  owner: "variant" | "product";
} {
  const match = METAFIELD_HEADER.exec(column);
  if (match) {
    return {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      namespace: match[1]!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      key: match[2]!,
      owner: /\(product\.metafields\./.test(column) ? "product" : "variant",
    };
  }
  return {
    namespace: "custom",
    owner: "variant" as const,
    key: column
      .toLowerCase()
      .replace(/\(.*\)/g, "")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, ""),
  };
}

function defaultDestinationFor(column: string): Destination {
  const normalized = column.toLowerCase().replace(/[^a-z0-9]/g, "");
  for (const [destination, aliases] of FIELD_COLUMN_ALIASES) {
    if (aliases.includes(normalized)) {
      return destination;
    }
  }
  // A column that names a product metafield defaults to writing one; silently
  // retargeting it to the variant writes a field nothing was reading.
  return deriveMetafield(column).owner === "product"
    ? "productMetafield"
    : "metafield";
}

function toTarget(
  column: string,
  config: ColumnConfig,
): ColumnTarget | undefined {
  if (
    config.destination === "metafield" ||
    config.destination === "productMetafield"
  ) {
    return config.namespace && config.key
      ? {
          kind: "metafield",
          column,
          owner:
            config.destination === "productMetafield" ? "product" : "variant",
          namespace: config.namespace,
          key: config.key,
          type: config.type,
          clearBlanks: config.clearBlanks,
        }
      : undefined;
  }
  return {
    kind: "field",
    column,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    field: config.destination.slice("field:".length) as VariantField,
    clearBlanks: config.clearBlanks,
  };
}

function statusBadge(row: ColumnRow, config: ColumnConfig): RedoBadgeProps {
  if (row.filledRows === 0) {
    return { color: "warning", size: "sm", text: "Empty in every row" };
  }
  if (config.destination === "metafield" && !config.key) {
    return { color: "warning", size: "sm", text: "Needs a key" };
  }
  return { color: "success", size: "sm", text: `${row.filledRows} values` };
}

const warningColor: Record<string, "warning" | "error" | "gray"> = {
  unmatched: "warning",
  ambiguous: "warning",
  "type-mismatch": "error",
  "gid-not-found": "error",
  "no-value": "gray",
  "variants-truncated": "warning",
};

/** Header a Shopify export uses for each match strategy's key. */
const STRATEGY_KEY_HEADERS: Record<MatchStrategy, string[]> = {
  sku: ["variant sku", "sku"],
  variantId: ["shopify variant id", "variant id", "id"],
  handleAndOptions: ["handle"],
  titleAndOptions: ["title"],
};

/** Columns a Shopify export uses to name a variant within its product. */
const OPTION_HEADERS = ["option1 value", "option2 value", "option3 value"];

function headerMatching(headers: string[], candidates: string[]) {
  for (const candidate of candidates) {
    const found = headers.find(
      (header) => header.trim().toLowerCase() === candidate,
    );
    if (found) {
      return found;
    }
  }
  return undefined;
}

/**
 * Searchable picker over the file's unmapped headers. A Shopify export has
 * 100+ of them, so the row count and a sample make the useful ones findable.
 */
const AddColumnPicker = memo(function AddColumnPicker({
  guesses,
  mappedColumns,
  onAdd,
}: {
  guesses: ColumnRow[];
  mappedColumns: string[];
  onAdd: (column: string) => void;
}) {
  const [searchString, setSearchString] = useState("");

  const options = useMemo(() => {
    const search = searchString.trim().toLowerCase();
    return guesses
      .filter((row) => !mappedColumns.includes(row.column))
      .filter((row) => !search || row.column.toLowerCase().includes(search))
      .map((row) => ({
        id: row.column,
        type: "text" as const,
        value: row.column,
        text: row.filledRows
          ? `${row.column} — ${row.filledRows} values`
          : `${row.column} — empty`,
      }));
  }, [guesses, mappedColumns, searchString]);

  return (
    <RedoSingleSelectDropdownInput<string>
      options={options}
      optionSelected={({ item }) => {
        if (item && typeof item !== "string" && item.value) {
          onAdd(item.value);
          setSearchString("");
        }
      }}
      placeholder="Add column"
      searchPlaceholder="Search columns"
      searchString={searchString}
      selectedItem={undefined}
      setSearchString={setSearchString}
      size="sm"
    />
  );
});

/** Says, in plain words, what the current match settings will actually do. */
function matchSentence(
  strategy: MatchStrategy,
  keyColumn: string | null,
  optionColumns: (string | null)[],
): string {
  if (!keyColumn) {
    return "Choose the column holding the value that identifies each variant.";
  }
  const noun = {
    sku: "the variant whose SKU",
    variantId: "the variant whose Shopify ID",
    handleAndOptions: "the product whose handle",
    titleAndOptions: "the product whose title",
  }[strategy];

  const base = `Each row is matched to ${noun} equals its "${keyColumn}" cell`;
  if (!usesOptions(strategy)) {
    return `${base}.`;
  }
  const chosen = optionColumns.filter((column): column is string => !!column);
  if (chosen.length === 0) {
    return `${base} — but with no option columns set, a row can only match a product with a single variant.`;
  }
  return `${base}, then to the variant inside it whose options equal ${chosen
    .map((column) => `"${column}"`)
    .join(" and ")}.`;
}

export const ShopifyBulkVariantEditCard = memo(
  function ShopifyBulkVariantEditCard({
    teamId,
  }: {
    teamId: string | undefined;
  }) {
    const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<ImportResult | null>(null);
    const [configs, setConfigs] = useState<Record<string, ColumnConfig>>({});
    const [mappedColumns, setMappedColumns] = useState<string[]>([]);
    const [strategy, setStrategy] = useState<MatchStrategy>("sku");
    const [keyColumn, setKeyColumn] = useState<string | null>(null);
    const [optionColumns, setOptionColumns] = useState<(string | null)[]>([
      null,
      null,
      null,
    ]);
    const [ambiguityPolicy, setAmbiguityPolicy] =
      useState<AmbiguityPolicy>("skip");
    const [forwardFillColumn, setForwardFillColumn] = useState<string | null>(
      null,
    );
    const autoFilledRef = useRef(false);

    const headers = useMemo(() => result?.headers ?? [], [result?.headers]);
    const guesses: ColumnRow[] = useMemo(
      () => result?.columnGuesses ?? [],
      [result?.columnGuesses],
    );

    const configFor = useCallback(
      (column: string): ColumnConfig =>
        configs[column] ?? {
          ...DEFAULT_CONFIG,
          destination: defaultDestinationFor(column),
          ...deriveMetafield(column),
          type:
            guesses.find((row) => row.column === column)?.guessedType ??
            DEFAULT_CONFIG.type,
        },
      [configs, guesses],
    );

    // A Shopify export runs to 100+ columns, so the table lists only the ones
    // deliberately added rather than every header with a "Don't import" row.
    const mappedRows: ColumnRow[] = mappedColumns.map(
      (column) =>
        guesses.find((row) => row.column === column) ?? {
          column,
          guessedType: DEFAULT_CONFIG.type,
          filledRows: 0,
          sampleValues: [],
        },
    );

    const targets = useMemo(
      () =>
        mappedColumns
          .map((column) => toTarget(column, configFor(column)))
          .filter((target): target is ColumnTarget => !!target),
      [mappedColumns, configFor],
    );

    const columnOptions = useMemo(
      () => [
        listItem<string | null>(null, "None"),
        ...headers.map((header) => listItem<string | null>(header, header)),
      ],
      [headers],
    );

    const canRun = !!file && !!keyColumn && targets.length > 0;
    // Product-only mappings change no variants, so gating on variants alone
    // made a product metafield import impossible to apply.
    const changeCount =
      (result?.variantsToChange ?? 0) + (result?.productsToChange ?? 0);
    const readyToApply =
      result?.planned && !result.applied && changeCount > 0 && canRun;

    /** Why Apply is unavailable, so a greyed-out button is never a dead end. */
    const applyBlockedBecause = (): string | undefined => {
      if (readyToApply || loading) {
        return undefined;
      }
      if (!file) {
        return "Upload a CSV to start.";
      }
      if (targets.length === 0) {
        return "Add at least one column to write, under Choose what to write.";
      }
      if (!keyColumn) {
        return "Pick the column that identifies each variant.";
      }
      if (!result?.planned) {
        return "Validate first — Apply turns on once there are changes to make.";
      }
      if (result.applied) {
        return "This import has already been applied. Validate again to make another pass.";
      }
      if (changeCount === 0) {
        return result.rowsMatched === 0
          ? "No row matched a variant in this store. Check the match settings above."
          : "Every matched variant already holds these values, so there is nothing to write.";
      }
      return undefined;
    };
    const applyBlockedReason = applyBlockedBecause();

    const resetForNewFile = (nextFile: File | null) => {
      setFile(nextFile);
      setResult(null);
      setConfigs({});
      setMappedColumns([]);
      autoFilledRef.current = false;
      setKeyColumn(null);
      setOptionColumns([null, null, null]);
    };

    const uploadState: FileUploadState<File>[] = file
      ? [
          {
            name: file.name,
            value: file,
            pendingBytes: false,
            bytesTotal: file.size,
          },
        ]
      : [];

    const addMappedColumn = (column: string) => {
      setMappedColumns((current) =>
        current.includes(column) ? current : [...current, column],
      );
    };

    const removeMappedColumn = (column: string) => {
      setMappedColumns((current) => current.filter((item) => item !== column));
    };

    const setConfig = (column: string, patch: Partial<ColumnConfig>) => {
      setConfigs((current) => ({
        ...current,
        [column]: { ...configFor(column), ...patch },
      }));
    };

    const handleExportCatalog = useHandler(async () => {
      if (!teamId || !rpcClient) {
        return;
      }
      try {
        setLoading(true);
        const response = await rpcClient.importCatalogCsv({
          teamId,
          dryRun: true,
          targets,
          ambiguityPolicy,
          exportCatalog: true,
        });
        if (response.errors.length > 0) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          toast(response.errors[0]!, { variant: "error" });
          return;
        }
        downloadCsv(response.exportCsv, "catalog-current-values.csv");
        toast(
          `Exported ${response.exportVariantCount} variants` +
            (response.exportMetafieldKeys.length > 0
              ? ` with ${response.exportMetafieldKeys.join(", ")}`
              : "") +
            (response.exportTruncatedRows.length > 0
              ? ` — ${response.exportTruncatedRows.length} row(s) have more metafields than one page, so their columns are incomplete`
              : ""),
          {
            variant:
              response.exportTruncatedRows.length > 0 ? "error" : "success",
          },
        );
      } catch (error) {
        toast("Catalog export failed", { variant: "error" });
        console.error("Catalog export failed:", error);
      } finally {
        setLoading(false);
      }
    });

    const handleDownloadSnapshot = useHandler(() => {
      if (result?.snapshotCsv) {
        downloadCsv(result.snapshotCsv, "catalog-current-values.csv");
      }
    });

    const run = useHandler(async (dryRun: boolean) => {
      if (!teamId || !rpcClient || !file) {
        return;
      }
      try {
        setLoading(true);
        const response = await rpcClient.importCatalogCsv(
          {
            teamId,
            dryRun,
            targets,
            ambiguityPolicy,
            forwardFillFromColumn: forwardFillColumn ?? undefined,
            match: keyColumn
              ? {
                  strategy,
                  keyColumn,
                  optionColumns: usesOptions(strategy)
                    ? optionColumns.filter(
                        (column): column is string => !!column,
                      )
                    : [],
                }
              : undefined,
          },
          { files: [file] },
        );
        setResult(response);

        // A ref, not `result`: that closure holds the value from before this
        // call, so it would misreport whether the file has been read yet.
        if (!autoFilledRef.current) {
          autoFilledRef.current = true;
          const handle = headerMatching(response.headers, ["handle"]);
          if (handle) {
            setForwardFillColumn((current) => current ?? handle);
          }
          setKeyColumn(
            (current) =>
              current ??
              headerMatching(
                response.headers,
                STRATEGY_KEY_HEADERS[strategy],
              ) ??
              null,
          );
          setOptionColumns((current) =>
            current.map(
              (value, index) =>
                value ??
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                headerMatching(response.headers, [OPTION_HEADERS[index]!]) ??
                null,
            ),
          );
        }

        if (response.errors.length > 0) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          toast(response.errors[0]!, { variant: "error" });
        } else if (response.applied) {
          toast(
            `Wrote ${response.valuesWritten} values across ${response.variantsToChange} variants`,
            { variant: "success" },
          );
        } else if (targets.length > 0) {
          toast(
            `${response.variantsToChange} variants would change — nothing written yet`,
            { variant: "success" },
          );
        }
      } catch (error) {
        toast("Catalog CSV import failed", { variant: "error" });
        console.error("Catalog CSV import failed:", error);
      } finally {
        setLoading(false);
      }
    });

    const mappingColumns: RedoTableColumn<ColumnRow>[] = [
      {
        key: "column",
        widthMode: { type: "fill", minLength: "160px" },
        renderHeaderCell: () => (
          <StandardRedoTableHeaderCell headerText="CSV column" />
        ),
        renderNormalCell: ({ row }) => (
          <RedoTableTextCell
            subtext={row.sampleValues.join(", ")}
            text={row.column}
          />
        ),
      },
      {
        key: "destination",
        widthMode: { type: "fill", minLength: "200px" },
        renderHeaderCell: () => (
          <StandardRedoTableHeaderCell headerText="Write to" />
        ),
        renderNormalCell: ({ row }) => {
          const options = DESTINATIONS.map((destination) =>
            listItem(destination.value, destination.label),
          );
          return (
            // Tighter than RedoTablePaddedCell, whose p="xl" overflows a sm row.
            <Flex align="center" flex={1} px="xl" py="sm">
              <RedoSingleSelectDropdownInput<Destination>
                fullWidth
                options={options}
                optionSelected={({ item }) => {
                  if (item && typeof item !== "string" && item.value) {
                    setConfig(row.column, { destination: item.value });
                  }
                }}
                selectedItem={options.find(
                  (option) =>
                    option.value === configFor(row.column).destination,
                )}
                size="xs"
              />
            </Flex>
          );
        },
      },
      {
        key: "metafield",
        widthMode: { type: "fill", minLength: "220px" },
        renderHeaderCell: () => (
          <StandardRedoTableHeaderCell headerText="Namespace / key / type" />
        ),
        renderNormalCell: ({ row }) => {
          const config = configFor(row.column);
          if (
            config.destination !== "metafield" &&
            config.destination !== "productMetafield"
          ) {
            return <RedoTableTextCell text="—" />;
          }
          const typeOptions = METAFIELD_TYPES.map((type) =>
            listItem(type, type),
          );
          return (
            <Flex align="center" flex={1} gap="xs" px="xl" py="sm">
              <RedoTextInput
                placeholder="namespace"
                setValue={(value) =>
                  setConfig(row.column, { namespace: value })
                }
                size="sm"
                value={config.namespace}
              />
              <RedoTextInput
                placeholder="key"
                setValue={(value) => setConfig(row.column, { key: value })}
                size="sm"
                value={config.key}
              />
              <RedoSingleSelectDropdownInput<string>
                options={typeOptions}
                optionSelected={({ item }) => {
                  if (item && typeof item !== "string" && item.value) {
                    setConfig(row.column, { type: item.value });
                  }
                }}
                selectedItem={typeOptions.find(
                  (option) => option.value === config.type,
                )}
                size="xs"
              />
            </Flex>
          );
        },
      },
      {
        key: "status",
        widthMode: { type: "hug" },
        renderHeaderCell: () => (
          <StandardRedoTableHeaderCell headerText="Data" />
        ),
        renderNormalCell: ({ row }) => (
          <RedoTableBadgeCell
            badges={[statusBadge(row, configFor(row.column))]}
          />
        ),
      },
      {
        key: "blanks",
        widthMode: { type: "hug" },
        renderHeaderCell: () => (
          <StandardRedoTableHeaderCell headerText="Blank cells" />
        ),
        renderNormalCell: ({ row }) => {
          const config = configFor(row.column);
          return (
            <Flex align="center" gap="sm" px="md" py="sm">
              <RedoCheckbox
                setValue={(checked) =>
                  setConfig(row.column, { clearBlanks: checked === true })
                }
                size="sm"
                value={config.clearBlanks}
              />
              <Text fontSize="xs" textColor="secondary">
                {config.clearBlanks ? "clear the value" : "leave alone"}
              </Text>
            </Flex>
          );
        },
      },
      {
        key: "remove",
        widthMode: { type: "hug" },
        renderHeaderCell: () => <StandardRedoTableHeaderCell headerText="" />,
        renderNormalCell: ({ row }) => (
          <Flex align="center" px="md" py="sm">
            <RedoButton
              aria-label={`Stop writing ${row.column}`}
              hierarchy="tertiary"
              IconLeading={TrashIcon}
              onClick={() => removeMappedColumn(row.column)}
              size="xs"
              theme="destructive"
            />
          </Flex>
        ),
      },
    ];

    const previewColumns: RedoTableColumn<
      ImportResult["previewChanges"][number]
    >[] = [
      {
        key: "product",
        widthMode: { type: "fill", minLength: "180px" },
        renderHeaderCell: () => (
          <StandardRedoTableHeaderCell headerText="Variant" />
        ),
        renderNormalCell: ({ row }) => (
          <RedoTableTextCell
            subtext={row.variantTitle}
            text={row.productTitle}
          />
        ),
      },
      {
        key: "changes",
        widthMode: { type: "fill", minLength: "260px" },
        renderHeaderCell: () => (
          <StandardRedoTableHeaderCell headerText="Change" />
        ),
        renderNormalCell: ({ row }) => (
          <RedoTableTextCell
            text={row.values
              .map(
                (value) =>
                  `${value.label}: ${value.before || "—"} → ${value.after}`,
              )
              .join(" · ")}
          />
        ),
      },
    ];

    return (
      <Card title="Shopify bulk variant edit">
        <Flex dir="column" gap="lg">
          <Text fontSize="sm" textColor="secondary">
            Writes variant metafields and fields from any CSV — the thing
            Shopify's own importer and bulk editor cannot do. Unmapped columns
            are left alone.
          </Text>

          <Flex align="center" gap="md">
            <RedoButton
              disabled={!teamId || loading}
              hierarchy="secondary"
              IconLeading={DownloadIcon}
              onClick={handleExportCatalog}
              pending={loading}
              size="sm"
              text="Export current values"
              theme="normal"
            />
            <Text fontSize="xs" textColor="secondary">
              Every variant with the fields and metafields it already has. Edit
              a column and upload it back — no file needed to start.
            </Text>
          </Flex>

          <RedoFileUpload
            accept=".csv"
            acceptMessage="CSV file"
            disabled={loading}
            files={uploadState}
            fullWidth
            handleFileDeleted={() => resetForNewFile(null)}
            handleFilesAdded={(files) => {
              const selected = files?.[0];
              if (selected) {
                resetForNewFile(selected);
              }
            }}
            hideDropzoneAfterSelection
          />

          {file && !result && (
            <RedoButton
              disabled={loading}
              hierarchy="secondary"
              onClick={() => run(true)}
              pending={loading}
              size="md"
              text="Read columns"
              theme="normal"
            />
          )}

          {headers.length > 0 && (
            <Flex dir="column" gap="md">
              <Text fontSize="sm" fontWeight="medium">
                1. Point each CSV row at a Shopify variant
              </Text>
              <Text fontSize="xs" textColor="secondary">
                Pick the column that identifies the variant. Nothing is written
                for a row whose value finds no variant.
              </Text>
              <Flex gap="sm" wrap="wrap">
                <RedoSingleSelectDropdownInput<MatchStrategy>
                  description="What identifies the variant a row is about."
                  label="Find the variant by"
                  options={STRATEGIES.map((option) =>
                    listItem(option.value, option.label),
                  )}
                  optionSelected={({ item }) => {
                    if (item && typeof item !== "string" && item.value) {
                      setStrategy(item.value);
                      const derived = headerMatching(
                        headers,
                        STRATEGY_KEY_HEADERS[item.value],
                      );
                      if (derived) {
                        setKeyColumn(derived);
                      }
                    }
                  }}
                  selectedItem={STRATEGIES.map((option) =>
                    listItem(option.value, option.label),
                  ).find((option) => option.value === strategy)}
                  size="sm"
                />
                <RedoSingleSelectDropdownInput<string | null>
                  description="Which column in your file holds that value."
                  label="…held in this column"
                  options={columnOptions}
                  optionSelected={({ item }) => {
                    if (item && typeof item !== "string") {
                      setKeyColumn(item.value);
                    }
                  }}
                  selectedItem={columnOptions.find(
                    (option) => option.value === keyColumn,
                  )}
                  size="sm"
                />
              </Flex>

              {usesOptions(strategy) && (
                <Flex dir="column" gap="xs">
                  <Text fontSize="xs" textColor="secondary">
                    A title names a product, and a product has several variants.
                    "Trackstar Pants" alone cannot say whether you mean Small,
                    Medium or Large — these columns do.
                  </Text>
                  <Flex gap="sm" wrap="wrap">
                    {optionColumns.map((selected, index) => (
                      <RedoSingleSelectDropdownInput<string | null>
                        key={index}
                        label={`Option ${index + 1} (e.g. ${["Size", "Colour", "Style"][index]})`}
                        options={columnOptions}
                        optionSelected={({ item }) => {
                          if (item && typeof item !== "string") {
                            setOptionColumns((current) =>
                              current.map((value, position) =>
                                position === index ? item.value : value,
                              ),
                            );
                          }
                        }}
                        selectedItem={columnOptions.find(
                          (option) => option.value === selected,
                        )}
                        size="sm"
                      />
                    ))}
                  </Flex>
                </Flex>
              )}

              <Text fontSize="xs" textColor="secondary">
                {matchSentence(strategy, keyColumn, optionColumns)}
              </Text>

              <RedoCollapseSubsection title="Edge cases">
                <Flex gap="sm" pt="sm" wrap="wrap">
                  <RedoSingleSelectDropdownInput<AmbiguityPolicy>
                    description="When one key matches several variants."
                    label="Ambiguous keys"
                    options={AMBIGUITY.map((option) =>
                      listItem(option.value, option.label),
                    )}
                    optionSelected={({ item }) => {
                      if (item && typeof item !== "string" && item.value) {
                        setAmbiguityPolicy(item.value);
                      }
                    }}
                    selectedItem={AMBIGUITY.map((option) =>
                      listItem(option.value, option.label),
                    ).find((option) => option.value === ambiguityPolicy)}
                    size="sm"
                  />
                  <RedoSingleSelectDropdownInput<string | null>
                    description="A Shopify export leaves Title, Vendor and product metafields blank on a product's second and later rows. Grouping by Handle copies them down."
                    label="Fill blank product cells down, grouped by"
                    options={columnOptions}
                    optionSelected={({ item }) => {
                      if (item && typeof item !== "string") {
                        setForwardFillColumn(item.value);
                      }
                    }}
                    selectedItem={columnOptions.find(
                      (option) => option.value === forwardFillColumn,
                    )}
                    size="sm"
                  />
                </Flex>
              </RedoCollapseSubsection>
            </Flex>
          )}

          {headers.length > 0 && (
            <Flex dir="column" gap="sm">
              <Flex align="center" gap="md" justify="space-between">
                <Text fontSize="sm" fontWeight="medium">
                  2. Choose what to write
                </Text>
                <AddColumnPicker
                  guesses={guesses}
                  mappedColumns={mappedColumns}
                  onAdd={addMappedColumn}
                />
              </Flex>
              {mappedRows.length === 0 ? (
                <Text fontSize="xs" textColor="secondary">
                  {headers.length} columns in this file. Add the ones to write —
                  everything else is left alone.
                </Text>
              ) : (
                <RedoStandaloneTable<ColumnRow>
                  layout={{ columns: mappingColumns, headerColor: "grey" }}
                  size="sm"
                  tableData={mappedRows}
                />
              )}
            </Flex>
          )}

          {result?.planned && result.ownershipNotes.length > 0 && (
            <Flex dir="column" gap="sm">
              <Text fontSize="sm" fontWeight="medium">
                Where these will be written
              </Text>
              {result.ownershipNotes.map((note) => (
                <RedoAlert
                  color={note.kind === "owner-mismatch" ? "error" : "warning"}
                  fullWidth
                  key={note.targetId + note.kind}
                  size="sm"
                  title={note.message}
                />
              ))}
            </Flex>
          )}

          {result?.planned && result.suspicions.length > 0 && (
            <Flex dir="column" gap="sm">
              <Text fontSize="sm" fontWeight="medium">
                Check these before applying
              </Text>
              {result.suspicions.map((item) => (
                <RedoAlert
                  color="warning"
                  description={`${item.count} in ${item.label} — ${item.samples
                    .slice(0, 4)
                    .join("; ")}${item.count > 4 ? "; …" : ""}`}
                  fullWidth
                  key={`${item.kind}-${item.label}`}
                  size="sm"
                  title={item.message}
                />
              ))}
            </Flex>
          )}

          {result?.planned &&
            result.warnings.map((warn) => (
              <RedoAlert
                color={warningColor[warn.kind] ?? "warning"}
                description={`${warn.count}: ${warn.samples.slice(0, 5).join(", ")}${
                  warn.count > 5 ? ", …" : ""
                }`}
                fullWidth
                key={warn.kind}
                size="sm"
                title={warn.message}
              />
            ))}

          {result?.planned && (
            <Flex dir="column" gap="xs">
              <Text fontSize="sm" fontWeight="medium">
                {result.applied ? "Import result" : "Validation result"}
              </Text>
              <Text fontSize="sm" textColor="secondary">
                Rows read: {result.rowsParsed} · Matched: {result.rowsMatched} ·
                Already correct: {result.rowsUnchanged} · Variants to change:{" "}
                {result.variantsToChange} · Products to change:{" "}
                {result.productsToChange} · Values to write:{" "}
                {result.valuesToWrite}
              </Text>
              {result.applied && (
                <Text fontSize="sm" textColor="secondary">
                  Written: {result.valuesWritten} · Failed:{" "}
                  {result.valuesFailed}
                </Text>
              )}
              {result.errors.map((error) => (
                <Text fontSize="xs" key={error} textColor="error">
                  {error}
                </Text>
              ))}
            </Flex>
          )}

          {result && result.previewChanges.length > 0 && (
            <Flex dir="column" gap="sm">
              <Text fontSize="xs" textColor="secondary">
                First {result.previewChanges.length} of{" "}
                {result.variantsToChange} variants
              </Text>
              <RedoStandaloneTable<ImportResult["previewChanges"][number]>
                layout={{ columns: previewColumns, headerColor: "grey" }}
                size="sm"
                tableData={result.previewChanges}
              />
            </Flex>
          )}

          {result && result.snapshotCsv.length > 0 && (
            <Flex align="center" gap="md">
              <RedoButton
                hierarchy="secondary"
                IconLeading={DownloadIcon}
                onClick={handleDownloadSnapshot}
                size="sm"
                text="Download current values"
                theme="normal"
              />
              <Text fontSize="xs" textColor="secondary">
                {result.snapshotRowCount} matched variants as they stand now —
                the backup, and re-importable to undo this run.
                {result.createdValueCount > 0
                  ? ` ${result.createdValueCount} are empty today; a blank cell is skipped, not cleared.`
                  : ""}
              </Text>
            </Flex>
          )}

          <Flex align="center" gap="md">
            <RedoButton
              disabled={!canRun || loading}
              hierarchy="secondary"
              onClick={() => run(true)}
              pending={loading}
              size="md"
              text="Validate"
              theme="normal"
            />
            <RedoButton
              disabled={!readyToApply || loading}
              hierarchy="primary"
              onClick={() => run(false)}
              pending={loading}
              size="md"
              text={
                readyToApply
                  ? `Apply ${result.valuesToWrite} value${result.valuesToWrite === 1 ? "" : "s"}`
                  : "Apply"
              }
              theme="normal"
            />
            {applyBlockedReason && (
              <Text fontSize="xs" textColor="secondary">
                {applyBlockedReason}
              </Text>
            )}
          </Flex>
        </Flex>
      </Card>
    );
  },
);
